import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    IconButton,
    Tooltip,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Button,
    Grid,
    Tabs,
    Tab,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    InputAdornment,
    TablePagination,
    Alert
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import PaymentIcon from '@mui/icons-material/Payment';
import SubscriptionsIcon from '@mui/icons-material/Subscriptions';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import EventIcon from '@mui/icons-material/Event';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { fetchWithAuth } from '../../services/authService';
import { API_BASE_URL } from '../../services/apiConfig';

interface Payment {
    paymentId: string;
    userId: string;
    userEmail: string;
    amount: number;
    mode: 'single' | 'subscription';
    stripeStatus: string;
    customerId: string | null;
    paidAt: string;
    activities: any;
}

interface PaymentStats {
    totalPayments: number;
    totalRevenue: number;
    paidCount: number;
    failedCount: number;
    averageAmount: string;
}

interface PaymentDetails {
    dynamodb: any;
    stripe: any;
}

interface Subscription {
    subscriptionId: string;
    customerId: string;
    customerEmail: string;
    status: string;
    currentPeriodStart: string;
    currentPeriodEnd: string;
    nextBillingDate: string;
    cancelAtPeriodEnd: boolean;
    canceledAt: string | null;
    created: string;
    amount: number;
    currency: string;
    interval: string;
    priceId: string;
    daysUntilRenewal: number;
}

interface Dispute {
    disputeId: string;
    amount: number;
    currency: string;
    status: string;
    reason: string;
    created: string;
    evidenceDueBy: string | null;
    isChargeRefundable: boolean;
    charge: string;
    paymentIntentId: string;
}

interface UpcomingCharge {
    subscriptionId: string;
    customerId: string;
    customerEmail: string;
    amount: number;
    currency: string;
    periodStart: string;
    periodEnd: string;
    dueDate: string;
    daysUntilDue: number;
    attemptCount: number;
    invoiceUrl: string;
}

export const AdminPayments: React.FC = () => {
    const [activeTab, setActiveTab] = useState(0);
    const [payments, setPayments] = useState<Payment[]>([]);
    const [filteredPayments, setFilteredPayments] = useState<Payment[]>([]);
    const [stats, setStats] = useState<PaymentStats | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [modeFilter, setModeFilter] = useState<string>('all');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [selectedPayment, setSelectedPayment] = useState<PaymentDetails | null>(null);
    const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
    const [error, setError] = useState<string>('');
    
    // Nuovi stati per subscription, disputes e upcoming charges
    const [stripeSubscriptions, setStripeSubscriptions] = useState<Subscription[]>([]);
    const [disputes, setDisputes] = useState<Dispute[]>([]);
    const [disputeStats, setDisputeStats] = useState<any>(null);
    const [upcomingCharges, setUpcomingCharges] = useState<UpcomingCharge[]>([]);
    const [totalNext30Days, setTotalNext30Days] = useState<number>(0);

    useEffect(() => {
        fetchPayments();
        fetchStripeSubscriptions();
        fetchDisputes();
        fetchUpcomingCharges();
    }, []);

    useEffect(() => {
        applyFilters();
    }, [payments, searchTerm, statusFilter, modeFilter]);

    const fetchPayments = async () => {
        setError('');
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/admin/payments`);
            if (!response.ok) throw new Error('Errore caricamento pagamenti');
            
            const data = await response.json();
            setPayments(data.payments || []);
            setStats(data.stats || null);
        } catch (err: any) {
            console.error('❌ Errore caricamento pagamenti:', err);
            setError(err.message || 'Errore durante il caricamento');
        }
    };

    const fetchStripeSubscriptions = async () => {
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/admin/active-subscriptions`);
            if (!response.ok) throw new Error('Errore caricamento subscription');
            
            const data = await response.json();
            setStripeSubscriptions(data.subscriptions || []);
        } catch (err: any) {
            console.error('❌ Errore caricamento subscription:', err);
        }
    };

    const fetchDisputes = async () => {
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/admin/disputes`);
            if (!response.ok) throw new Error('Errore caricamento dispute');
            
            const data = await response.json();
            setDisputes(data.disputes || []);
            setDisputeStats(data.stats || null);
        } catch (err: any) {
            console.error('❌ Errore caricamento dispute:', err);
        }
    };

    const fetchUpcomingCharges = async () => {
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/admin/upcoming-charges`);
            if (!response.ok) throw new Error('Errore caricamento addebiti futuri');
            
            const data = await response.json();
            setUpcomingCharges(data.charges || []);
            setTotalNext30Days(data.totalNext30Days || 0);
        } catch (err: any) {
            console.error('❌ Errore caricamento addebiti:', err);
        }
    };

    const applyFilters = () => {
        let filtered = [...payments];

        // Search filter (email o paymentId)
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(p => 
                p.userEmail.toLowerCase().includes(term) ||
                p.paymentId.toLowerCase().includes(term) ||
                p.userId.toLowerCase().includes(term)
            );
        }

        // Status filter
        if (statusFilter !== 'all') {
            filtered = filtered.filter(p => p.stripeStatus === statusFilter);
        }

        // Mode filter
        if (modeFilter !== 'all') {
            filtered = filtered.filter(p => p.mode === modeFilter);
        }

        setFilteredPayments(filtered);
        setPage(0); // Reset pagination
    };

    const handleViewDetails = async (payment: Payment) => {
        try {
            const response = await fetchWithAuth(
                `${API_BASE_URL}/admin/payment-details?paymentId=${payment.paymentId}`
            );
            if (!response.ok) throw new Error('Errore caricamento dettagli');
            
            const details = await response.json();
            setSelectedPayment(details);
            setDetailsDialogOpen(true);
        } catch (err: any) {
            console.error('❌ Errore caricamento dettagli:', err);
            alert('Errore durante il caricamento dei dettagli');
        }
    };

    const exportToCSV = () => {
        const headers = ['Data', 'Email Utente', 'Tipo', 'Importo (€)', 'Stato', 'Payment ID', 'Customer ID'];
        const rows = filteredPayments.map(p => [
            new Date(p.paidAt).toLocaleDateString('it-IT'),
            p.userEmail,
            p.mode === 'subscription' ? 'Abbonamento' : 'Singolo',
            p.amount.toFixed(2),
            p.stripeStatus,
            p.paymentId,
            p.customerId || 'N/A'
        ]);

        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `pagamenti_${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
    };

    const getStatusChip = (status: string) => {
        const statusMap: { [key: string]: { label: string; color: any; icon: any } } = {
            'paid': { label: 'Pagato', color: 'success', icon: <CheckCircleIcon fontSize="small" /> },
            'succeeded': { label: 'Successo', color: 'success', icon: <CheckCircleIcon fontSize="small" /> },
            'pending': { label: 'In Attesa', color: 'warning', icon: <EventIcon fontSize="small" /> },
            'failed': { label: 'Fallito', color: 'error', icon: <ErrorIcon fontSize="small" /> },
            'canceled': { label: 'Cancellato', color: 'default', icon: <ErrorIcon fontSize="small" /> },
            'requires_payment_method': { label: 'Richiede Metodo', color: 'warning', icon: <WarningIcon fontSize="small" /> },
        };

        const config = statusMap[status] || { label: status, color: 'default', icon: null };
        return (
            <Chip 
                label={config.label} 
                color={config.color}
                size="small"
                icon={config.icon}
            />
        );
    };

    const getModeChip = (mode: string) => {
        return (
            <Chip 
                label={mode === 'subscription' ? 'Abbonamento' : 'Singolo'} 
                color={mode === 'subscription' ? 'primary' : 'default'}
                size="small"
                icon={mode === 'subscription' ? <SubscriptionsIcon /> : <PaymentIcon />}
                variant="outlined"
            />
        );
    };

    const paginatedPayments = filteredPayments.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    // Subscription attive (filtro locale per ora)
    const activeSubscriptions = payments.filter(
        p => p.mode === 'subscription' && p.stripeStatus === 'paid'
    );

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                    <Typography variant="h4" gutterBottom sx={{ color: '#8B6F47', fontWeight: 600 }}>
                        Gestione Pagamenti
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Monitoraggio completo di pagamenti, subscription e addebiti
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="Esporta CSV">
                        <IconButton onClick={exportToCSV} color="primary" size="large">
                            <DownloadIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Aggiorna dati">
                        <IconButton 
                            onClick={() => {
                                fetchPayments();
                                fetchStripeSubscriptions();
                                fetchDisputes();
                                fetchUpcomingCharges();
                            }} 
                            color="primary" 
                            size="large"
                        >
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            {/* Statistiche Cards */}
            {stats && (
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card sx={{ bgcolor: '#f5f5f5' }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <PaymentIcon sx={{ color: '#8B6F47' }} />
                                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                        Totale Pagamenti
                                    </Typography>
                                </Box>
                                <Typography variant="h4" fontWeight={600} sx={{ color: '#8B6F47' }}>
                                    {stats.totalPayments}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card sx={{ bgcolor: '#e8f5e9' }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <TrendingUpIcon sx={{ color: '#4caf50' }} />
                                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                        Ricavi Totali
                                    </Typography>
                                </Box>
                                <Typography variant="h4" fontWeight={600} sx={{ color: '#4caf50' }}>
                                    €{stats.totalRevenue.toFixed(2)}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card sx={{ bgcolor: '#e3f2fd' }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <CheckCircleIcon sx={{ color: '#2196f3' }} />
                                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                        Pagamenti Riusciti
                                    </Typography>
                                </Box>
                                <Typography variant="h4" fontWeight={600} sx={{ color: '#2196f3' }}>
                                    {stats.paidCount}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card sx={{ bgcolor: '#ffebee' }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <ErrorIcon sx={{ color: '#f44336' }} />
                                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                        Pagamenti Falliti
                                    </Typography>
                                </Box>
                                <Typography variant="h4" fontWeight={600} sx={{ color: '#f44336' }}>
                                    {stats.failedCount}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}

            {/* Tabs */}
            <Card>
                <Tabs 
                    value={activeTab} 
                    onChange={(_, newValue) => setActiveTab(newValue)}
                    sx={{ borderBottom: 1, borderColor: 'divider' }}
                    variant="scrollable"
                    scrollButtons="auto"
                >
                    <Tab 
                        label={`Tutti i Pagamenti (${filteredPayments.length})`} 
                        icon={<PaymentIcon />}
                        iconPosition="start"
                    />
                    <Tab 
                        label={`Subscription Attive (${stripeSubscriptions.length})`} 
                        icon={<SubscriptionsIcon />}
                        iconPosition="start"
                    />
                    <Tab 
                        label={`Addebiti Futuri (${upcomingCharges.length})`} 
                        icon={<EventIcon />}
                        iconPosition="start"
                    />
                    <Tab 
                        label={`Dispute (${disputes.length})`} 
                        icon={<WarningIcon />}
                        iconPosition="start"
                    />
                </Tabs>

                <CardContent>
                    {/* Filtri */}
                    <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                        <TextField
                            placeholder="Cerca per email, ID pagamento o utente..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            size="small"
                            sx={{ flex: '1 1 300px' }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                )
                            }}
                        />
                        <FormControl size="small" sx={{ minWidth: 150 }}>
                            <InputLabel>Stato</InputLabel>
                            <Select
                                value={statusFilter}
                                label="Stato"
                                onChange={(e) => setStatusFilter(e.target.value)}
                            >
                                <MenuItem value="all">Tutti</MenuItem>
                                <MenuItem value="paid">Pagato</MenuItem>
                                <MenuItem value="succeeded">Successo</MenuItem>
                                <MenuItem value="pending">In Attesa</MenuItem>
                                <MenuItem value="failed">Fallito</MenuItem>
                                <MenuItem value="canceled">Cancellato</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 150 }}>
                            <InputLabel>Tipo</InputLabel>
                            <Select
                                value={modeFilter}
                                label="Tipo"
                                onChange={(e) => setModeFilter(e.target.value)}
                            >
                                <MenuItem value="all">Tutti</MenuItem>
                                <MenuItem value="single">Singolo</MenuItem>
                                <MenuItem value="subscription">Abbonamento</MenuItem>
                            </Select>
                        </FormControl>
                        {(searchTerm || statusFilter !== 'all' || modeFilter !== 'all') && (
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() => {
                                    setSearchTerm('');
                                    setStatusFilter('all');
                                    setModeFilter('all');
                                }}
                            >
                                Reset Filtri
                            </Button>
                        )}
                    </Box>

                    {/* Tab Panel 0: Tutti i Pagamenti */}
                    {activeTab === 0 && (
                        <>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                            <TableCell><strong>Data</strong></TableCell>
                                            <TableCell><strong>Utente</strong></TableCell>
                                            <TableCell><strong>Tipo</strong></TableCell>
                                            <TableCell align="right"><strong>Importo</strong></TableCell>
                                            <TableCell><strong>Stato</strong></TableCell>
                                            <TableCell><strong>Payment ID</strong></TableCell>
                                            <TableCell align="center"><strong>Azioni</strong></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {paginatedPayments.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={7} align="center">
                                                    <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                                                        Nessun pagamento trovato
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            paginatedPayments.map((payment) => (
                                                <TableRow key={payment.paymentId} hover>
                                                    <TableCell>
                                                        {new Date(payment.paidAt).toLocaleDateString('it-IT', {
                                                            day: '2-digit',
                                                            month: '2-digit',
                                                            year: 'numeric'
                                                        })}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" fontWeight={500}>
                                                            {payment.userEmail}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {payment.userId}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>{getModeChip(payment.mode)}</TableCell>
                                                    <TableCell align="right">
                                                        <Typography variant="body2" fontWeight={600}>
                                                            €{payment.amount.toFixed(2)}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>{getStatusChip(payment.stripeStatus)}</TableCell>
                                                    <TableCell>
                                                        <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                                            {payment.paymentId}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Tooltip title="Visualizza dettagli">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleViewDetails(payment)}
                                                                color="primary"
                                                            >
                                                                <VisibilityIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <TablePagination
                                component="div"
                                count={filteredPayments.length}
                                page={page}
                                onPageChange={(_, newPage) => setPage(newPage)}
                                rowsPerPage={rowsPerPage}
                                onRowsPerPageChange={(e) => {
                                    setRowsPerPage(parseInt(e.target.value, 10));
                                    setPage(0);
                                }}
                                rowsPerPageOptions={[10, 25, 50, 100]}
                                labelRowsPerPage="Righe per pagina:"
                                labelDisplayedRows={({ from, to, count }) => `${from}-${to} di ${count}`}
                            />
                        </>
                    )}

                    {/* Tab Panel 1: Subscription Attive da Stripe */}
                    {activeTab === 1 && (
                        <>
                            <Alert severity="info" sx={{ mb: 2 }}>
                                <strong>Subscription Attive da Stripe:</strong> Dati in tempo reale con prossime date di rinnovo.
                            </Alert>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                            <TableCell><strong>Email Cliente</strong></TableCell>
                                            <TableCell><strong>Subscription ID</strong></TableCell>
                                            <TableCell align="right"><strong>Importo</strong></TableCell>
                                            <TableCell><strong>Prossimo Rinnovo</strong></TableCell>
                                            <TableCell align="center"><strong>Giorni Mancanti</strong></TableCell>
                                            <TableCell><strong>Stato</strong></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {stripeSubscriptions.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={6} align="center">
                                                    <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                                                        Nessuna subscription attiva trovata
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            stripeSubscriptions.map((sub) => (
                                                <TableRow key={sub.subscriptionId} hover>
                                                    <TableCell>
                                                        <Typography variant="body2" fontWeight={500}>
                                                            {sub.customerEmail}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {sub.customerId}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                                            {sub.subscriptionId}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Typography variant="body2" fontWeight={600}>
                                                            €{(sub.amount / 100).toFixed(2)}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            /{sub.interval}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2">
                                                            {new Date(sub.nextBillingDate).toLocaleDateString('it-IT', {
                                                                day: '2-digit',
                                                                month: 'short',
                                                                year: 'numeric'
                                                            })}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Chip 
                                                            label={`${sub.daysUntilRenewal} giorni`}
                                                            size="small"
                                                            color={sub.daysUntilRenewal <= 7 ? 'warning' : 'default'}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        {sub.cancelAtPeriodEnd ? (
                                                            <Chip label="Cancella a Fine Periodo" size="small" color="warning" />
                                                        ) : (
                                                            <Chip label={sub.status} size="small" color="success" />
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </>
                    )}

                    {/* Tab Panel 2: Addebiti Futuri */}
                    {activeTab === 2 && (
                        <>
                            <Alert severity="success" sx={{ mb: 2 }}>
                                <strong>Prossimi 30 Giorni:</strong> Previsti €{(totalNext30Days / 100).toFixed(2)} in addebiti
                            </Alert>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                            <TableCell><strong>Email Cliente</strong></TableCell>
                                            <TableCell><strong>Subscription ID</strong></TableCell>
                                            <TableCell align="right"><strong>Importo</strong></TableCell>
                                            <TableCell><strong>Data Addebito</strong></TableCell>
                                            <TableCell align="center"><strong>Giorni Mancanti</strong></TableCell>
                                            <TableCell><strong>Periodo</strong></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {upcomingCharges.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={6} align="center">
                                                    <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                                                        Nessun addebito futuro programmato
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            upcomingCharges.map((charge, idx) => (
                                                <TableRow 
                                                    key={idx} 
                                                    hover
                                                    sx={{
                                                        bgcolor: charge.daysUntilDue <= 7 ? 'rgba(255, 152, 0, 0.08)' : 'transparent'
                                                    }}
                                                >
                                                    <TableCell>
                                                        <Typography variant="body2" fontWeight={500}>
                                                            {charge.customerEmail}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {charge.customerId}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                                            {charge.subscriptionId}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Typography variant="body2" fontWeight={600}>
                                                            €{(charge.amount / 100).toFixed(2)}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" fontWeight={500}>
                                                            {new Date(charge.dueDate).toLocaleDateString('it-IT', {
                                                                day: '2-digit',
                                                                month: 'short',
                                                                year: 'numeric'
                                                            })}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Chip 
                                                            label={`${charge.daysUntilDue} giorni`}
                                                            size="small"
                                                            color={
                                                                charge.daysUntilDue <= 3 ? 'error' :
                                                                charge.daysUntilDue <= 7 ? 'warning' : 'default'
                                                            }
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="caption">
                                                            {new Date(charge.periodStart).toLocaleDateString('it-IT')} - {new Date(charge.periodEnd).toLocaleDateString('it-IT')}
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </>
                    )}

                    {/* Tab Panel 3: Dispute/Contenziosi */}
                    {activeTab === 3 && (
                        <>
                            {disputeStats && (
                                <Grid container spacing={2} sx={{ mb: 2 }}>
                                    <Grid item xs={12} sm={4}>
                                        <Alert severity="warning">
                                            <strong>Richiedono Risposta:</strong> {disputeStats.needsResponse}
                                        </Alert>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Alert severity="success">
                                            <strong>Vinte:</strong> {disputeStats.won}
                                        </Alert>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Alert severity="error">
                                            <strong>Perse:</strong> {disputeStats.lost}
                                        </Alert>
                                    </Grid>
                                </Grid>
                            )}
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                            <TableCell><strong>Dispute ID</strong></TableCell>
                                            <TableCell align="right"><strong>Importo</strong></TableCell>
                                            <TableCell><strong>Motivo</strong></TableCell>
                                            <TableCell><strong>Stato</strong></TableCell>
                                            <TableCell><strong>Data Creazione</strong></TableCell>
                                            <TableCell><strong>Scadenza Prove</strong></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {disputes.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={6} align="center">
                                                    <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                                                        ✅ Nessuna dispute trovata - Ottimo!
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            disputes.map((dispute) => (
                                                <TableRow key={dispute.disputeId} hover>
                                                    <TableCell>
                                                        <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                                            {dispute.disputeId}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Typography variant="body2" fontWeight={600}>
                                                            €{(dispute.amount / 100).toFixed(2)}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip label={dispute.reason} size="small" variant="outlined" />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip 
                                                            label={dispute.status}
                                                            size="small"
                                                            color={
                                                                dispute.status === 'needs_response' ? 'error' :
                                                                dispute.status === 'won' ? 'success' :
                                                                dispute.status === 'lost' ? 'error' : 'warning'
                                                            }
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        {new Date(dispute.created).toLocaleDateString('it-IT')}
                                                    </TableCell>
                                                    <TableCell>
                                                        {dispute.evidenceDueBy ? (
                                                            <Typography variant="body2" color="error" fontWeight={500}>
                                                                {new Date(dispute.evidenceDueBy).toLocaleDateString('it-IT')}
                                                            </Typography>
                                                        ) : (
                                                            <Typography variant="caption" color="text.secondary">
                                                                N/A
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </>
                    )}
                </CardContent>
            </Card>

            {/* Dialog Dettagli Pagamento */}
            <Dialog 
                open={detailsDialogOpen} 
                onClose={() => setDetailsDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle sx={{ bgcolor: '#8B6F47', color: 'white' }}>
                    Dettagli Pagamento
                </DialogTitle>
                <DialogContent sx={{ mt: 2 }}>
                    {selectedPayment && (
                        <Box>
                            {/* DynamoDB Data */}
                            <Typography variant="h6" gutterBottom sx={{ color: '#8B6F47', mt: 1 }}>
                                Dati Sistema
                            </Typography>
                            <Paper sx={{ p: 2, bgcolor: '#f5f5f5', mb: 2 }}>
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary">Payment ID</Typography>
                                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                            {selectedPayment.dynamodb.paymentId}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary">User ID</Typography>
                                        <Typography variant="body2">{selectedPayment.dynamodb.userId}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary">Email Utente</Typography>
                                        <Typography variant="body2">{selectedPayment.dynamodb.userEmail}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary">Importo</Typography>
                                        <Typography variant="body2" fontWeight={600}>
                                            €{selectedPayment.dynamodb.amount.toFixed(2)}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary">Tipo</Typography>
                                        <Typography variant="body2">{selectedPayment.dynamodb.mode}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary">Stato</Typography>
                                        <Typography variant="body2">{selectedPayment.dynamodb.stripeStatus}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary">Data Pagamento</Typography>
                                        <Typography variant="body2">
                                            {new Date(selectedPayment.dynamodb.paidAt).toLocaleString('it-IT')}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary">Customer ID</Typography>
                                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                            {selectedPayment.dynamodb.customerId || 'N/A'}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Paper>

                            {/* Stripe Data */}
                            {selectedPayment.stripe && (
                                <>
                                    <Typography variant="h6" gutterBottom sx={{ color: '#8B6F47' }}>
                                        Dati Stripe
                                    </Typography>
                                    <Paper sx={{ p: 2, bgcolor: '#e3f2fd' }}>
                                        <pre style={{ fontSize: '0.85rem', overflow: 'auto' }}>
                                            {JSON.stringify(selectedPayment.stripe, null, 2)}
                                        </pre>
                                    </Paper>
                                </>
                            )}

                            {/* Activities */}
                            {selectedPayment.dynamodb.activities && (
                                <>
                                    <Typography variant="h6" gutterBottom sx={{ color: '#8B6F47', mt: 2 }}>
                                        Attività Associate ({selectedPayment.dynamodb.activities.length})
                                    </Typography>
                                    <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                                        {selectedPayment.dynamodb.activities.map((activity: any, idx: number) => (
                                            <Box key={idx} sx={{ mb: 1 }}>
                                                <Typography variant="body2" fontWeight={500}>
                                                    {activity.indirizzo}, {activity.citta} ({activity.provincia})
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Paper>
                                </>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDetailsDialogOpen(false)}>Chiudi</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
